import React, { useState } from 'react';
import { 
  DocumentTextIcon, 
  DocumentArrowDownIcon, 
  ShieldCheckIcon, 
  UserGroupIcon, 
  CurrencyRupeeIcon,
  BuildingOfficeIcon,
  CloudArrowUpIcon 
} from '@heroicons/react/24/outline';
import { getCurrentFinancialYear } from '../../utils/financialYear';

const EsiReport = () => {
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedFinancialYear, setSelectedFinancialYear] = useState(getCurrentFinancialYear());
  const [reportType, setReportType] = useState('monthly');

  // Mock data - will be replaced with API call
  const esiData = {
    summary: {
      totalEmployees: 3,
      totalEmployeeContribution: 681, // 0.75% of gross
      totalEmployerContribution: 3270, // 3.25% of gross
      totalEsiDeduction: 3951,
      totalGrossWages: 136000,
      averageContributionPerEmployee: 227
    },
    employees: [
      {
        employeeId: 'EMP001',
        employeeName: 'Dorothy Sanchez',
        department: 'Sales',
        grossSalary: 45333,
        esiWages: 45333,
        employeeContribution: 227, // 0.75% of gross (rounded)
        employerContribution: 1090, // 3.25% of gross (rounded)
        ipNumber: 'IP001234567',
        esiNumber: 'ESI001',
        status: 'Active'
      },
      {
        employeeId: 'EMP002',
        employeeName: 'Maria Martinez',
        department: 'Design',
        grossSalary: 45333,
        esiWages: 45333,
        employeeContribution: 227,
        employerContribution: 1090,
        ipNumber: 'IP001234568',
        esiNumber: 'ESI002',
        status: 'Active'
      },
      {
        employeeId: 'EMP003',
        employeeName: 'Jane Moore',
        department: 'Quality Assurance',
        grossSalary: 45334,
        esiWages: 45334,
        employeeContribution: 227,
        employerContribution: 1090,
        ipNumber: 'IP001234569',
        esiNumber: 'ESI003',
        status: 'Active'
      }
    ]
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const handleDownloadESIFile = () => {
    // Generate ESI upload file format
    const esiFileData = esiData.employees.map(emp => ({
      'IP Number': emp.ipNumber,
      'Employee Name': emp.employeeName,
      'ESI Number': emp.esiNumber,
      'Days Worked': 30,
      'Total Monthly Wages': emp.grossSalary,
      'ESI Wages': emp.esiWages,
      'Employee Contribution': emp.employeeContribution,
      'Employer Contribution': emp.employerContribution,
      'Total Contribution': emp.employeeContribution + emp.employerContribution,
      'Reason for Zero Wages': '',
      'Last Working Day': '',
      'Remarks': ''
    }));

    // Convert to CSV
    const headers = Object.keys(esiFileData[0]);
    const csvContent = [
      headers.join(','),
      ...esiFileData.map(row => headers.map(header => row[header]).join(','))
    ].join('\n');

    // Download file
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ESI_Upload_${selectedMonth}_${selectedYear}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadReport = () => {
    // Generate ESI report CSV
    const reportData = esiData.employees.map(emp => ({
      'Employee ID': emp.employeeId,
      'Employee Name': emp.employeeName,
      'Department': emp.department,
      'IP Number': emp.ipNumber,
      'ESI Number': emp.esiNumber,
      'Gross Salary': emp.grossSalary,
      'ESI Wages': emp.esiWages,
      'Employee Contribution (0.75%)': emp.employeeContribution,
      'Employer Contribution (3.25%)': emp.employerContribution,
      'Total Contribution': emp.employeeContribution + emp.employerContribution,
      'Status': emp.status
    }));

    const headers = Object.keys(reportData[0]);
    const csvContent = [
      headers.join(','),
      ...reportData.map(row => headers.map(header => row[header]).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ESI_Report_${selectedMonth}_${selectedYear}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-4 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div className="w-full lg:w-auto">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-gray-900 to-indigo-900 bg-clip-text text-transparent mb-2">
            ESI Report
          </h1>
          <p className="text-gray-600 text-sm sm:text-base lg:text-lg">Employee State Insurance contributions and upload file generation</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
          <button
            onClick={handleDownloadESIFile}
            className="flex items-center justify-center px-4 sm:px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-xl font-medium transition-all duration-300 hover:shadow-lg hover:scale-105 text-sm sm:text-base"
          >
            <CloudArrowUpIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
            Download Upload File
          </button>
          <button
            onClick={handleDownloadReport}
            className="flex items-center justify-center px-4 sm:px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl font-medium transition-all duration-300 hover:shadow-lg hover:scale-105 text-sm sm:text-base"
          >
            <DocumentTextIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
            Download Report
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-white/20 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="text-xs sm:text-sm font-medium text-gray-600 mb-1">Total Employees</h3>
              <p className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                {esiData.summary.totalEmployees}
              </p>
            </div>
            <div className="p-3 sm:p-4 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl ml-3">
              <UserGroupIcon className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-white/20 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="text-xs sm:text-sm font-medium text-gray-600 mb-1">Employee Contribution</h3>
              <p className="text-xl sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                {formatCurrency(esiData.summary.totalEmployeeContribution)}
              </p>
              <p className="text-xs text-gray-500">0.75% of gross wages</p>
            </div>
            <div className="p-3 sm:p-4 bg-gradient-to-br from-green-100 to-emerald-100 rounded-2xl ml-3">
              <CurrencyRupeeIcon className="h-6 w-6 sm:h-8 sm:w-8 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-white/20 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="text-xs sm:text-sm font-medium text-gray-600 mb-1">Employer Contribution</h3>
              <p className="text-xl sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-purple-600 to-violet-600 bg-clip-text text-transparent">
                {formatCurrency(esiData.summary.totalEmployerContribution)}
              </p>
              <p className="text-xs text-gray-500">3.25% of gross wages</p>
            </div>
            <div className="p-3 sm:p-4 bg-gradient-to-br from-purple-100 to-violet-100 rounded-2xl ml-3">
              <BuildingOfficeIcon className="h-6 w-6 sm:h-8 sm:w-8 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-white/20 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="text-xs sm:text-sm font-medium text-gray-600 mb-1">Total ESI Deduction</h3>
              <p className="text-xl sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">
                {formatCurrency(esiData.summary.totalEsiDeduction)}
              </p>
              <p className="text-xs text-gray-500">4% of gross wages</p>
            </div>
            <div className="p-3 sm:p-4 bg-gradient-to-br from-orange-100 to-amber-100 rounded-2xl ml-3">
              <ShieldCheckIcon className="h-6 w-6 sm:h-8 sm:w-8 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filter Controls */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 sm:p-6 shadow-xl border border-white/20">
        <h3 className="text-lg sm:text-xl font-semibold bg-gradient-to-r from-gray-900 to-indigo-900 bg-clip-text text-transparent mb-4 sm:mb-6">
          Report Filters
        </h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 sm:mb-3">Report Type</label>
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white/50 backdrop-blur-sm transition-all duration-300 hover:shadow-md text-sm sm:text-base"
            >
              <option value="monthly">Monthly Report</option>
              <option value="annual">Annual Report</option>
              <option value="half-yearly">Half Yearly Report</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 sm:mb-3">Month</label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white/50 backdrop-blur-sm transition-all duration-300 hover:shadow-md text-sm sm:text-base"
              disabled={reportType === 'annual'}
            >
              {months.map((month, index) => (
                <option key={index} value={index + 1}>{month}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 sm:mb-3">Year</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white/50 backdrop-blur-sm transition-all duration-300 hover:shadow-md text-sm sm:text-base"
            >
              <option value="2025">2025</option>
              <option value="2024">2024</option>
              <option value="2023">2023</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 sm:mb-3">Financial Year</label>
            <select
              value={selectedFinancialYear}
              onChange={(e) => setSelectedFinancialYear(e.target.value)}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white/50 backdrop-blur-sm transition-all duration-300 hover:shadow-md text-sm sm:text-base"
              disabled={reportType === 'monthly'}
            >
              <option value="2025-26">2025-26</option>
              <option value="2024-25">2024-25</option>
              <option value="2023-24">2023-24</option>
            </select>
          </div>
        </div>
      </div>

      {/* Employee-wise ESI Details */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 sm:p-6 shadow-xl border border-white/20">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 gap-3">
          <h3 className="text-lg sm:text-xl font-semibold bg-gradient-to-r from-gray-900 to-indigo-900 bg-clip-text text-transparent">
            Employee-wise ESI Contributions
          </h3>
          <div className="text-xs sm:text-sm text-gray-500 bg-gray-100 px-3 sm:px-4 py-2 rounded-full">
            {months[selectedMonth - 1]} {selectedYear}
          </div>
        </div>

        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <div className="inline-block min-w-full align-middle">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                <tr>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider rounded-tl-xl">
                    Employee
                  </th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider hidden sm:table-cell">
                    IP / ESI Number
                  </th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    ESI Wages
                  </th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider hidden md:table-cell">
                    Employee Contribution
                    <div className="text-xs font-normal text-gray-400">(0.75%)</div>
                  </th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider hidden lg:table-cell">
                    Employer Contribution
                    <div className="text-xs font-normal text-gray-400">(3.25%)</div>
                  </th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Total Contribution
                  </th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider rounded-tr-xl">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {esiData.employees.map((employee) => (
                  <tr key={employee.employeeId} className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-300 hover:shadow-md">
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <div>
                        <div className="text-xs sm:text-sm font-semibold text-gray-900">
                          {employee.employeeName}
                        </div>
                        <div className="text-xs text-gray-500">
                          {employee.employeeId} • {employee.department}
                        </div>
                        <div className="text-xs text-gray-500 sm:hidden">
                          IP: {employee.ipNumber}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap hidden sm:table-cell">
                      <div className="text-xs sm:text-sm font-medium text-gray-900">{employee.ipNumber}</div>
                      <div className="text-xs text-gray-500">{employee.esiNumber}</div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-medium text-gray-900">
                      {formatCurrency(employee.esiWages)}
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-semibold text-green-600 hidden md:table-cell">
                      {formatCurrency(employee.employeeContribution)}
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-semibold text-purple-600 hidden lg:table-cell">
                      {formatCurrency(employee.employerContribution)}
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-semibold text-blue-600">
                      {formatCurrency(employee.employeeContribution + employee.employerContribution)}
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 sm:px-3 py-1 text-xs font-semibold rounded-full bg-gradient-to-r from-green-100 to-emerald-100 text-green-800">
                        {employee.status}
                      </span>
                    </td>
                  </tr>
                ))}
                {/* Total Row */}
                <tr className="bg-gradient-to-r from-gray-50 to-gray-100 font-semibold border-t-2 border-gray-300">
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap" colSpan="2">
                    <div className="text-sm sm:text-base font-bold text-gray-900">
                      Total ({esiData.employees.length} employees)
                    </div>
                  </td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm font-bold text-gray-900">
                    {formatCurrency(esiData.employees.reduce((sum, emp) => sum + emp.esiWages, 0))}
                  </td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm font-bold text-green-600 hidden md:table-cell">
                    {formatCurrency(esiData.summary.totalEmployeeContribution)}
                  </td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm font-bold text-purple-600 hidden lg:table-cell">
                    {formatCurrency(esiData.summary.totalEmployerContribution)}
                  </td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm font-bold text-blue-600">
                    {formatCurrency(esiData.summary.totalEsiDeduction)}
                  </td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 sm:px-3 py-1 text-xs font-semibold rounded-full bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800">
                      Summary
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ESI Upload File Information */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <div className="bg-gradient-to-r from-green-50/70 to-emerald-50/70 backdrop-blur-sm rounded-2xl p-4 sm:p-8 shadow-xl border border-green-100/50">
          <h3 className="text-lg sm:text-2xl font-semibold bg-gradient-to-r from-green-800 to-emerald-800 bg-clip-text text-transparent mb-4 sm:mb-6">
            ESI Upload File Format
          </h3>
          <div>
            <h4 className="text-base sm:text-lg font-semibold text-gray-800 mb-2 sm:mb-3">File Contents</h4>
            <ul className="text-xs sm:text-sm text-gray-600 space-y-2 sm:space-y-3">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-green-500 rounded-full mt-1.5 sm:mt-2 mr-2 sm:mr-3 flex-shrink-0"></span>
                IP Number and Employee Details
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-green-500 rounded-full mt-1.5 sm:mt-2 mr-2 sm:mr-3 flex-shrink-0"></span>
                Monthly wages and ESI wages
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-green-500 rounded-full mt-1.5 sm:mt-2 mr-2 sm:mr-3 flex-shrink-0"></span>
                Employee and Employer contributions
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-green-500 rounded-full mt-1.5 sm:mt-2 mr-2 sm:mr-3 flex-shrink-0"></span>
                Days worked and contribution period
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-green-500 rounded-full mt-1.5 sm:mt-2 mr-2 sm:mr-3 flex-shrink-0"></span>
                CSV format ready for ESIC portal upload
              </li>
            </ul>
          </div>
        </div>

        <div className="bg-gradient-to-r from-blue-50/70 to-indigo-50/70 backdrop-blur-sm rounded-2xl p-4 sm:p-8 shadow-xl border border-blue-100/50">
          <h3 className="text-lg sm:text-2xl font-semibold bg-gradient-to-r from-blue-800 to-indigo-800 bg-clip-text text-transparent mb-4 sm:mb-6">
            ESI Compliance Notes
          </h3>
          <div>
            <h4 className="text-base sm:text-lg font-semibold text-gray-800 mb-2 sm:mb-3">Important Guidelines</h4>
            <ul className="text-xs sm:text-sm text-gray-600 space-y-2 sm:space-y-3">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-orange-500 rounded-full mt-1.5 sm:mt-2 mr-2 sm:mr-3 flex-shrink-0"></span>
                Employee contribution: 0.75% of gross wages
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-orange-500 rounded-full mt-1.5 sm:mt-2 mr-2 sm:mr-3 flex-shrink-0"></span>
                Employer contribution: 3.25% of gross wages
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-orange-500 rounded-full mt-1.5 sm:mt-2 mr-2 sm:mr-3 flex-shrink-0"></span>
                ESI ceiling: ₹25,000 per month (as of 2024)
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-orange-500 rounded-full mt-1.5 sm:mt-2 mr-2 sm:mr-3 flex-shrink-0"></span>
                File must be uploaded by 15th of following month
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-orange-500 rounded-full mt-1.5 sm:mt-2 mr-2 sm:mr-3 flex-shrink-0"></span>
                Verify IP numbers before submission
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Contribution Rates Info */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 sm:p-6 shadow-xl border border-white/20">
        <h3 className="text-lg sm:text-xl font-semibold bg-gradient-to-r from-gray-900 to-indigo-900 bg-clip-text text-transparent mb-4 sm:mb-6">
          ESI Contribution Rates
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          <div className="text-center p-4 sm:p-6 bg-gradient-to-br from-green-50/70 to-emerald-50/70 backdrop-blur-sm rounded-2xl border border-green-100/50 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
            <div className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-2">0.75%</div>
            <div className="text-sm sm:text-base text-gray-600 mb-1">Employee Contribution</div>
            <div className="text-xs text-gray-500">Deducted from salary</div>
          </div>
          <div className="text-center p-4 sm:p-6 bg-gradient-to-br from-purple-50/70 to-violet-50/70 backdrop-blur-sm rounded-2xl border border-purple-100/50 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
            <div className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-purple-600 to-violet-600 bg-clip-text text-transparent mb-2">3.25%</div>
            <div className="text-sm sm:text-base text-gray-600 mb-1">Employer Contribution</div>
            <div className="text-xs text-gray-500">Additional cost to company</div>
          </div>
          <div className="text-center p-4 sm:p-6 bg-gradient-to-br from-blue-50/70 to-indigo-50/70 backdrop-blur-sm rounded-2xl border border-blue-100/50 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
            <div className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-2">4.00%</div>
            <div className="text-sm sm:text-base text-gray-600 mb-1">Total Contribution</div>
            <div className="text-xs text-gray-500">Combined rate</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EsiReport;